import React from 'react';

import * as api from '@gitbook/api-client';
import type * as analytics from '@gitbook/app-analytics/client';
import type { NavigationScreenSpaceState } from '@gitbook/app-navigation';
import { Icon } from '@gitbook/spine-icons';
import {
    BreadcrumbsSkeleton,
    Button,
    HeaderBar,
    HeaderBarActions,
    HeaderBarActionsMain,
    HeaderBarActionsSide,
    HeaderBarBreadcrumbs,
    HeaderBarMain,
    HeaderBarTabs,
} from '@gitbook/spine-react';
import { assertNever } from '@gitbook/util-ts';

import {
    ChangeRequestActionsButton,
    ChangeRequestMergeButtons,
    ChangeRequestReviewButton,
    ChangeRequestUnarchiveButton,
    VariablesSidePanelButton,
} from '../../../Components';
import { useLoggedInUser } from '../../../state';
import { ChangeRequestAgentButton } from '../Agent';
import { ChangeRequestOverviewButton } from '../ChangeRequest';
import { CommentsSideSheetButton } from '../Comments';
import { TableOfContentsMobileToggleButton } from '../TableOfContents';
import { BreadcrumbsChangeRequest } from './BreadcrumbsChangeRequest';
// import { BrokenLinksButton } from './BrokenLinksButton';
import { SpaceHistoryButton } from './SpaceHistoryButton';
import { SpaceRealtimeParticipants } from './SpaceRealtimeParticipants';
import { ViewModeButton } from './ViewModeButton';

/**
 * Header for a change request.
 */
export function HeaderChangeRequest(props: {
    screen: NavigationScreenSpaceState;
    space: api.Space;
    changeRequest: api.ChangeRequest;
    analyticsContext: analytics.AnalyticsContextSpaceContent;
}) {
    const { screen, space, changeRequest, analyticsContext } = props;
    const user = useLoggedInUser();
    const showCRSubmitReviewButton =
        space.permissions.review && user.uid !== changeRequest.createdBy.id;

    return (
        <HeaderBar breakpoint={showCRSubmitReviewButton ? '5xl' : '4xl'}>
            <HeaderBarMain breakpoint={showCRSubmitReviewButton ? '5xl' : '4xl'}>
                <TableOfContentsMobileToggleButton />

                <HeaderBarBreadcrumbs>
                    <React.Suspense fallback={<BreadcrumbsSkeleton items={3} />}>
                        <BreadcrumbsChangeRequest space={space} changeRequest={changeRequest} />
                    </React.Suspense>
                </HeaderBarBreadcrumbs>

                <HeaderBarTabs breakpoint={showCRSubmitReviewButton ? '5xl' : '4xl'}>
                    <ViewModeButton screen={screen} space={space} changeRequest={changeRequest} />

                    <ChangeRequestActionsButton
                        kind="blank"
                        size="small"
                        changeRequest={changeRequest}
                        space={space}
                        leadingIcon={Icon.MoreHorizontal}
                    />
                </HeaderBarTabs>
            </HeaderBarMain>
            <HeaderBarActions breakpoint={showCRSubmitReviewButton ? '5xl' : '4xl'}>
                <HeaderBarActionsSide breakpoint={showCRSubmitReviewButton ? '5xl' : '4xl'}>
                    <SpaceRealtimeParticipants />
                    {/* Overview */}
                    <ChangeRequestOverviewButton
                        space={space}
                        changeRequest={changeRequest}
                        screen={screen}
                    />

                    {/* AI */}
                    <ChangeRequestAgentButton
                        space={space}
                        changeRequest={changeRequest}
                        screen={screen}
                    />

                    {/* Variables */}
                    <VariablesSidePanelButton screen={screen} />

                    {/* Comments */}
                    {space.permissions.comment ? (
                        <CommentsSideSheetButton
                            kind="blank"
                            size="medium"
                            space={space}
                            changeRequest={changeRequest}
                            screen={screen}
                            title="View all comments in this change request"
                        />
                    ) : null}

                    {/* Audits */}
                    {/* <BrokenLinksButton space={space} changeRequest={changeRequest} /> */}

                    {/* History */}
                    <SpaceHistoryButton space={space} changeRequest={changeRequest} />
                </HeaderBarActionsSide>
                <HeaderBarActionsMain breakpoint={showCRSubmitReviewButton ? '5xl' : '4xl'}>
                    {(() => {
                        switch (changeRequest.status) {
                            case api.ChangeRequestStatus.Archived:
                                return (
                                    <ChangeRequestUnarchiveButton
                                        space={space}
                                        changeRequest={changeRequest}
                                    />
                                );
                            case api.ChangeRequestStatus.Open:
                            case api.ChangeRequestStatus.Draft:
                                return (
                                    <>
                                        {showCRSubmitReviewButton ? (
                                            <ChangeRequestReviewButton
                                                space={space.id}
                                                changeRequest={changeRequest.id}
                                                className="shrink-0"
                                            />
                                        ) : null}

                                        <ChangeRequestMergeButtons
                                            space={space}
                                            changeRequest={changeRequest}
                                            analyticsContext={analyticsContext}
                                            showMergeRulesStatus={
                                                // We don't want to show the merge rules status when the overview is open
                                                // as it's already shown in the side panel
                                                screen.sideSheet?.type !== 'overview'
                                            }
                                        />
                                    </>
                                );
                            case api.ChangeRequestStatus.Merged:
                                return (
                                    <Button
                                        kind="secondary"
                                        leadingIcon={Icon.ArrowLeft}
                                        href={{
                                            screen: {
                                                type: 'space',
                                                space: space.id,
                                                organization: space.organization,
                                                page: screen.page,
                                                changeRequest: undefined,
                                                sideSheet: undefined,
                                            },
                                        }}
                                        label="Back to primary content"
                                    />
                                );
                            default:
                                assertNever(
                                    changeRequest.status,
                                    `Unknown change request status: ${changeRequest.status}`
                                );
                        }
                    })()}
                </HeaderBarActionsMain>
            </HeaderBarActions>
        </HeaderBar>
    );
}
